import React, { useState } from "react";
import { connect } from "react-redux";
import { I18n, ConsoleLogger as Logger } from "@aws-amplify/core";
import { signIn } from "aws-amplify/auth";
import { withStyles } from "@material-ui/styles";
import { get, toLower, trim } from "lodash";
import Box from "@material-ui/core/Box";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";
import InputLabel from "@material-ui/core/InputLabel";
import Typography from "@material-ui/core/Typography";

import IncorrectInputMessage from "../Form/IncorrectInputMessage";
import CustomInputAuth from "./CustomInputAuth";
import { setKeepMeLoggedIn } from "../../actions/common";
import colors from "../../assets/style/colors";
import Dict from "../../resources/Dict";

I18n.setLanguage("en");
I18n.putVocabularies(Dict);

const logger = new Logger("SignIn");

const styles = {
  label: {
    fontSize: "14px",
    color: colors.black87,
  },
  keepMeLoggedIn: {
    textAlign: "center",
    marginTop: "25px",
  },
  helperText: {
    fontSize: "14px",
    color: colors.black87,
    marginTop: "8px",
    marginBottom: "8px",
  },
};

const CustomFormControlLabel = withStyles({
  label: {
    color: colors.black54,
    fontSize: "14px",
  },
})(FormControlLabel);

const CustomSignIn = (props) => {
  const [username, setUsername] = useState("");
  const [keepMeLoggedIn, setKeepMeLoggedInState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [incorrectForm, setIncorrectForm] = useState(null);

  const handleClickKeepMeLoggedIn = () => {
    setKeepMeLoggedInState((prev) => !prev);
  };

  const handleSignIn = async (event) => {
    if (event) {
      event.preventDefault();
    }

    const cleanUsername = username || "";
    if (cleanUsername === "") {
      setIncorrectForm("emptyUsername");
      return;
    }

    setLoading(true);
    setIncorrectForm(null);

    try {
      const { nextStep } = await signIn({
        username: cleanUsername,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "EMAIL_OTP",
        },
      });

      if (nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
        props.onAuthStateChange?.("confirmSignIn", { username: cleanUsername });

        return;
      }

      if (nextStep?.signInStep === "DONE") {
        props.onAuthStateChange?.("signedIn");

        return;
      }

      setIncorrectForm("authFailed");
    } catch (err) {
      if (
        err?.code === "UserNotFoundException" ||
        err?.code === "NotAuthorizedException"
      ) {
        setIncorrectForm("authFailed");
      } else {
        logger.debug("signIn error", err);
        setIncorrectForm("authFailed");
      }
    } finally {
      props.dispatch(
        setKeepMeLoggedIn({ keepMeLoggedIn: keepMeLoggedIn })
      );

      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignIn} noValidate className={props.classes.form}>
      <InputLabel htmlFor="username" className={props.classes.label} style={{ position: "relative", top: "-2px", fontWeight: "bold" }}>
        {I18n.get("auth_emailAddress")}
      </InputLabel>
      <Typography className={props.classes.helperText} component="p">
        {I18n.get("auth_emailOtpSignInHelp")}
      </Typography>
      <CustomInputAuth
        id="email"
        name="username"
        autoComplete="email"
        autoFocus
        onChange={(event) => {
          const val = toLower(trim(get(event, ["target", "value"])));
          setUsername(val);
        }}
        value={username}
      />
      {incorrectForm === "emptyUsername" && (
        <IncorrectInputMessage
          message={I18n.get("auth_pleaseEnterAnEmailAddress")}
        />
      )}
      {incorrectForm === "authFailed" && (
        <IncorrectInputMessage
          message={I18n.get("auth_incorrectEmailAddress")}
        />
      )}
      <Box className={props.classes.keepMeLoggedIn}>
        <CustomFormControlLabel
          control={<Checkbox value="remember" color="primary" />}
          label={I18n.get("auth_keepMeLoggedIn")}
          checked={keepMeLoggedIn}
          onChange={handleClickKeepMeLoggedIn}
        />
      </Box>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={loading}
        style={{ marginTop: "20px" }}
      >
        {I18n.get("auth_login")}
      </Button>
    </form>
  );
};

export default connect()(withStyles(styles)(CustomSignIn));
